import { Component, computed, forwardRef, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { GenericTypeAheadMultiselectComponent } from 'ngx-lib';

@Component({
    selector: 'app-reactive-list-options',
    templateUrl: './reactive-list-options.component.html',
    styleUrls: ['./reactive-list-options.component.scss'],
    imports: [ReactiveFormsModule, GenericTypeAheadMultiselectComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReactiveListOptionsComponent),
            multi: true
        }
    ]
})
export class ReactiveListOptionsComponent implements ControlValueAccessor {
    readonly label = input<string>('Options');
    readonly readonly = input<boolean>(false);
    readonly disabled = input<boolean>(false);
    readonly control = input<FormControl<string[]> | null>(null);

    readonly selectedOptions = signal<string[]>([]);
    readonly placeholder = computed(() => `Add ${this.label()}`);
    readonly selectedOptionsForLib = computed(() => this.selectedOptions().map((option) => ({ name: option })));

    private isDisabled = false;

    get isComponentDisabled(): boolean {
        return this.disabled() || this.isDisabled;
    }

    writeValue(value: string[] | null): void {
        if (Array.isArray(value) && value.length > 0) {
            const normalized = this.normalizeOptions(value);
            this.selectedOptions.set(normalized);
        } else {
            this.selectedOptions.set([]);
        }
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onOptionsChange(event: any[]): void {
        const normalizedOptions = this.normalizeOptions(event);
        this.selectedOptions.set(normalizedOptions);
        this.onChange(normalizedOptions);
        this.onTouched();
    }

    private normalizeOptions(options: any[]): string[] {
        if (!Array.isArray(options)) {
            return [];
        }
        return options
            .filter((option) => option != null && option !== '')
            .map((option) => {
                if (typeof option === 'string') {
                    return option.trim();
                }
                if (option && typeof option === 'object') {
                    const name = option.name ?? option.text ?? option.label ?? String(option);
                    return String(name).trim();
                }
                return String(option).trim();
            })
            .filter((option) => option.length > 0);
    }

    private onChange: (value: string[]) => void = () => {
        // registerOnChange()
    };

    private onTouched: () => void = () => {
        // registerOnTouched()
    };
}
